import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Broadcom.css";
import broadcomLogo from "../../assets/images/partners/Broadcom.jpg";

/* ── Animated Counter Hook ── */
function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}

/* ── Intersection Observer ── */
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Data ── */
const whyCards = [
  { icon: "🏗️", title: "VMware by Broadcom Expertise", body: "Our VCAP/VCP-certified engineers design and operate the complete VMware stack — from vSphere and vSAN through NSX and VMware Cloud Foundation." },
  { icon: "🔒", title: "Zero Trust Security", body: "NSX micro-segmentation and distributed firewall capabilities enable enterprise zero-trust network security built directly into the virtual infrastructure layer." },
  { icon: "☁️", title: "Private Cloud Acceleration", body: "VMware Cloud Foundation unifies compute, storage, and networking into a single private cloud platform deployable on-premises, at the edge, or in hosted data centers." },
  { icon: "⚡", title: "High Performance Computing", body: "vSphere with GPU passthrough and NUMA awareness delivers bare-metal performance for AI/ML workloads, real-time analytics, and demanding enterprise applications." },
  { icon: "🔄", title: "Seamless Hybrid Integration", body: "Connect on-premises VMware environments to AWS, Azure, and Google Cloud with consistent networking, security policies, and operational tooling across all environments." },
  { icon: "📊", title: "Intelligent Operations", body: "VMware Aria delivers unified observability across your entire infrastructure estate — log analytics, cost management, performance monitoring, and automated remediation." },
];

const solutions = [
  {
    name: "vSphere & vSAN",
    tag: "Compute & Storage",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=700&q=80",
    desc: "Industry-leading server virtualization combined with hyper-converged storage. Eliminate SAN complexity with policy-driven storage running directly on server hardware.",
    features: ["DRS & HA Clustering", "vMotion Live Migration", "VM Encryption", "Storage DRS"],
  },
  {
    name: "VMware NSX",
    tag: "Network Virtualization",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=700&q=80",
    desc: "Software-defined networking and security for the modern data center. NSX delivers micro-segmentation, zero-trust architecture, and complete network automation.",
    features: ["Micro-Segmentation", "Distributed Firewall", "Overlay Networking", "Federation"],
  },
  {
    name: "VMware Aria Suite",
    tag: "Cloud Management",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80",
    desc: "Unified cloud management platform for multi-cloud visibility and control. Monitor costs, performance, logs, and configurations across your entire infrastructure estate.",
    features: ["Log Intelligence", "Cost Management", "vROps Analytics", "Automation Orchestrator"],
  },
  {
    name: "VMware Cloud Foundation",
    tag: "Private Cloud",
    img: "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=700&q=80",
    desc: "The complete private cloud platform. VCF integrates vSphere, vSAN, NSX, and Aria in a single deployment model with automated lifecycle management.",
    features: ["SDDC Manager", "Workload Domains", "vLCM Lifecycle", "Cloud Builder"],
  },
  {
    name: "Carbon Black Security",
    tag: "Endpoint Security",
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=700&q=80",
    desc: "Next-generation endpoint protection and workload security. Real-time threat detection, behavioral analytics, and automated response for every workload in your environment.",
    features: ["EDR & XDR", "Behavioral Analytics", "Workload Protection", "NGAV"],
  },
  {
    name: "Tanzu Platform",
    tag: "Modern Apps & Kubernetes",
    img: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?auto=format&fit=crop&w=700&q=80",
    desc: "Enterprise Kubernetes platform for modern application delivery. Build, run, and manage containers across on-premises and cloud with full governance and developer productivity.",
    features: ["Kubernetes Management", "App Catalog", "Service Mesh", "Dev Portals"],
  },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant virtual infrastructure for EHR systems, medical imaging, and clinical applications with zero-trust security controls." },
  { icon: "🏦", name: "Financial Services", desc: "High-availability platforms for trading systems, core banking, and financial applications with regulatory compliance and audit trails." },
  { icon: "🎓", name: "Education", desc: "Scalable virtual desktop infrastructure and cloud platforms for campuses, research institutions, and distributed learning environments." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-aligned virtual infrastructure supporting secure government operations, inter-agency collaboration, and classified workloads." },
  { icon: "🛒", name: "Retail", desc: "Resilient multi-site virtual infrastructure supporting POS systems, e-commerce platforms, and supply chain applications at scale." },
  { icon: "🏭", name: "Manufacturing", desc: "Edge-to-cloud infrastructure for OT/IT convergence, industrial automation, and real-time analytics across factory floors." },
  { icon: "📡", name: "Telecommunications", desc: "NFV and network virtualization platforms enabling carriers to deliver scalable, software-defined network services efficiently." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Secure, resilient infrastructure for grid management, SCADA systems, and digital operations with OT security built-in." },
];

const deploymentSteps = [
  { num: "01", icon: "🔍", title: "Assessment", desc: "Evaluate your current virtualization environment, workload dependencies, and cloud readiness." },
  { num: "02", icon: "📐", title: "Planning", desc: "Design the VCF architecture, networking topology, and migration sequencing strategy." },
  { num: "03", icon: "🏗️", title: "Design", desc: "Produce detailed HLD/LLD documentation, HW BOM, and licensing specifications." },
  { num: "04", icon: "🚀", title: "Deployment", desc: "Implement the infrastructure, configure workload domains, and migrate virtual machines." },
  { num: "05", icon: "⚙️", title: "Optimization", desc: "Tune DRS, storage policies, NSX segments, and Aria operations for peak performance." },
  { num: "06", icon: "🛡️", title: "Support", desc: "Ongoing VMware-certified support, lifecycle management, and proactive health monitoring." },
];

const benefits = [
  { icon: "💰", title: "40% TCO Reduction", desc: "Eliminate physical network hardware with NSX and consolidate storage with vSAN to dramatically reduce infrastructure costs." },
  { icon: "⚡", title: "10x Faster Provisioning", desc: "Automate VM, network, and storage provisioning through Aria Automation — reducing time-to-deploy from days to minutes." },
  { icon: "🛡️", title: "Zero-Trust Security Built In", desc: "NSX micro-segmentation isolates every workload, eliminating east-west attack paths without physical firewalls." },
  { icon: "📈", title: "99.99% Uptime SLA", desc: "vSphere HA, vMotion, and Site Recovery Manager protect mission-critical workloads with RPO/RTO measured in minutes." },
];

const certifications = [
  { badge: "🏆", name: "VMware Cloud Foundation Expert", desc: "VCAP-level VCF architecture and deployment certification" },
  { badge: "⭐", name: "VMware Partner Network", desc: "Advanced-tier VMware partner with certified sales and technical teams" },
  { badge: "🔐", name: "NSX Security Specialist", desc: "Expert certification for VMware NSX micro-segmentation and zero trust" },
  { badge: "☁️", name: "Aria Operations Expert", desc: "Advanced multi-cloud management and operations platform certification" },
];

const useCases = [
  { title: "Private Cloud Modernization", desc: "Replace aging 3-tier infrastructure with a fully software-defined VCF private cloud, reducing sprawl and simplifying operations." },
  { title: "Data Center Consolidation", desc: "Consolidate multiple data centers into a unified VCF environment with consistent policy, governance, and operational model." },
  { title: "Disaster Recovery Automation", desc: "Implement Site Recovery Manager with automated runbooks to achieve sub-15-minute RTO for your most critical workloads." },
  { title: "Hybrid Cloud Extension", desc: "Stretch VMware workloads into AWS, Azure, or Google Cloud with VMware Cloud — using the same tools and policies as on-premises." },
  { title: "AI/ML Infrastructure", desc: "Deploy GPU-accelerated vSphere nodes with vGPU support for demanding AI training and inference workloads at enterprise scale." },
  { title: "DevSecOps Platform", desc: "Run Tanzu Kubernetes clusters alongside traditional VMs in the same VCF environment with shared networking and security policies." },
];

const faqData = [
  { q: "What is VMware Cloud Foundation (VCF) and why does it matter?", a: "VMware Cloud Foundation integrates vSphere, vSAN, NSX, and Aria Operations into a single platform with automated lifecycle management. It gives organizations a consistent private cloud operating model — eliminating silos between compute, storage, and networking teams and dramatically reducing operational complexity." },
  { q: "How does ALG Data Guard differ from other VMware partners?", a: "ALG Data Guard maintains VCAP-certified engineers for both design and deployment tracks. We specialize in VCF greenfield builds, migration projects, and NSX security implementations. Our engineers work exclusively with enterprise clients on complex multi-site architectures." },
  { q: "Can VMware NSX replace physical firewalls?", a: "NSX Distributed Firewall can enforce micro-segmentation and east-west security policies at the hypervisor level without physical firewall appliances. For north-south traffic, NSX Edge provides advanced firewall, NAT, and VPN capabilities. Most organizations use NSX to complement perimeter firewalls rather than eliminate them entirely." },
  { q: "How long does a typical VCF deployment take?", a: "A standard single-site VCF deployment with management and workload domains typically takes 4–8 weeks from kickoff to production readiness. This includes hardware procurement, Cloud Builder automation, SDDC Manager configuration, NSX overlay networking, and Aria integration. Complex multi-site or migration-heavy projects may take 3–6 months." },
  { q: "What licensing changes came with the Broadcom acquisition of VMware?", a: "Broadcom transitioned VMware to a subscription-based licensing model bundled into VCF Essentials, Standard, and Advanced editions. The new model consolidates vSphere, vSAN, NSX, and Aria licenses into unified subscription SKUs. ALG Data Guard helps clients evaluate licensing scenarios, model costs, and optimize their entitlement strategy." },
  { q: "Does ALG Data Guard offer managed VMware services?", a: "Yes. Our managed VMware service covers Day-2 operations including patch management, performance monitoring, capacity planning, incident response, and environment optimization. We offer both fully managed and co-managed models depending on your internal team's capabilities and preferences." },
];

/* ── Component ── */
export default function Broadcom() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);

  const c1 = useCounter(350, 2000, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(800, 2000, metricsInView);
  const c4 = useCounter(24, 2000, metricsInView);

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );

  const ChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );

  return (
    <div className="broadcom-v2-page pd-page">
      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={broadcomLogo} alt="Broadcom" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Virtualization & Cloud Infrastructure
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Broadcom (VMware)</h1>
            <p className="pd-identity-desc">
              Broadcom's VMware portfolio — vSphere, vSAN, NSX, Aria, and VMware Cloud Foundation — powers the world's most demanding private and hybrid cloud environments.
              ALG Data Guard's VMware-certified engineers design, migrate, and operate your entire virtual stack with precision and enterprise-grade reliability.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="bc-hero">
        <div className="bc-hero__bg-grid" aria-hidden="true" />
        <div className="bc-hero__bg-glow" aria-hidden="true" />
        <div className="bc-hero__bg-glow2" aria-hidden="true" />
        <div className="bc-hero__inner">
          <div className="bc-hero__content">
            <div className="bc-hero__kicker">
              <span className="bc-hero__kicker-dot" />
              VMware by Broadcom — Authorized Partner
            </div>
            <h2 className="bc-hero__title">
              Power Your <em>Private Cloud</em> with VMware by Broadcom
            </h2>
            <p className="bc-hero__body">
              From server virtualization to software-defined networking and intelligent cloud management,
              VMware's integrated platform delivers the agility of public cloud with the security and
              control of on-premises infrastructure. ALG Data Guard's VCAP-certified engineers
              deliver expert VMware architecture, design, migration, and lifecycle management.
            </p>
            <div className="bc-hero__actions">
              <button className="bc-btn-primary" onClick={() => navigate("/contact")}>
                Assess My VMware Environment <ArrowIcon />
              </button>
              <button className="bc-btn-outline" onClick={() => navigate("/contact")}>
                Talk to a VMware Expert
              </button>
            </div>
            <div className="bc-hero__stats">
              <div className="bc-hero__stat">
                <span className="bc-hero__stat-value">15+</span>
                <span className="bc-hero__stat-label">Years VMware Experience</span>
              </div>
              <div className="bc-hero__stat">
                <span className="bc-hero__stat-value">500+</span>
                <span className="bc-hero__stat-label">VMs Migrated</span>
              </div>
              <div className="bc-hero__stat">
                <span className="bc-hero__stat-value">99.99%</span>
                <span className="bc-hero__stat-label">Uptime Delivered</span>
              </div>
            </div>
          </div>

          <div className="bc-hero__visual">
            <div className="bc-hero__arch-card">
              <div className="bc-hero__arch-title">VMware Cloud Foundation Architecture</div>
              <div className="bc-arch-layer bc-arch-layer--cloud">
                <span>☁️</span>
                <div>
                  <div style={{ fontWeight: 700, color: "#fff", marginBottom: 4 }}>VMware Cloud Foundation (VCF)</div>
                  <div className="bc-arch-pills">
                    <span className="bc-arch-pill">SDDC Manager</span>
                    <span className="bc-arch-pill">Cloud Builder</span>
                    <span className="bc-arch-pill">vLCM</span>
                  </div>
                </div>
              </div>
              <div className="bc-arch-connector">↕</div>
              <div className="bc-arch-layer bc-arch-layer--platform">
                <span>🖥️</span>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Virtual Platform Layer</div>
                  <div className="bc-arch-pills">
                    <span className="bc-arch-pill">vSphere</span>
                    <span className="bc-arch-pill">vSAN</span>
                    <span className="bc-arch-pill">NSX</span>
                    <span className="bc-arch-pill">Aria</span>
                  </div>
                </div>
              </div>
              <div className="bc-arch-connector">↕</div>
              <div className="bc-arch-layer bc-arch-layer--infra">
                <span>🔩</span>
                <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>Physical Infrastructure — Dell | HPE | Cisco UCS</div>
              </div>
              <div style={{ marginTop: "1.2rem", textAlign: "center", fontSize: "0.8rem", color: "#ff6666", fontWeight: 600 }}>
                🛡️ Managed by ALG Data Guard — 24/7
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Broadcom ── */}
      <section className="bc-why">
        <div className="bc-shell">
          <div className="bc-why__head">
            <span className="bc-section-badge"><span className="bc-dot" /> Why Broadcom VMware</span>
            <h2 className="bc-section-title">The Gold Standard in Enterprise Virtualization</h2>
            <p className="bc-section-sub">
              VMware by Broadcom powers 80% of Fortune 500 workloads. Here's why leading organizations
              trust the VMware platform — and why ALG Data Guard is their partner of choice to implement it.
            </p>
          </div>
          <div className="bc-why__grid">
            {whyCards.map((card) => (
              <div key={card.title} className="bc-why-card">
                <div className="bc-why-card__icon">{card.icon}</div>
                <h3 className="bc-why-card__title">{card.title}</h3>
                <p className="bc-why-card__body">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solutions Overview ── */}
      <section className="bc-solutions">
        <div className="bc-shell">
          <div className="bc-solutions__head">
            <span className="bc-section-badge"><span className="bc-dot" /> VMware Portfolio</span>
            <h2 className="bc-section-title">The Complete VMware Stack</h2>
            <p className="bc-section-sub">
              ALG Data Guard's certified engineers design, deploy, and manage the full VMware portfolio —
              from server virtualization through software-defined networking and modern application platforms.
            </p>
          </div>
          <div className="bc-solutions__grid">
            {solutions.map((s) => (
              <div key={s.name} className="bc-solution-card">
                <div className="bc-solution-card__img-wrap">
                  <img src={s.img} alt={s.name} className="bc-solution-card__img" />
                  <span className="bc-solution-card__tag">{s.tag}</span>
                </div>
                <div className="bc-solution-card__body">
                  <h3 className="bc-solution-card__name">{s.name}</h3>
                  <p className="bc-solution-card__desc">{s.desc}</p>
                  <div className="bc-solution-card__features">
                    {s.features.map((f) => (
                      <span key={f} className="bc-solution-card__feature">{f}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industry Solutions ── */}
      <section className="bc-industries">
        <div className="bc-shell">
          <div className="bc-industries__head">
            <span className="bc-section-badge" style={{ background: "rgba(204,0,0,0.2)", borderColor: "rgba(204,0,0,0.4)", color: "#ff6666" }}><span className="bc-dot" /> Industries We Serve</span>
            <h2 className="bc-section-title">Proven Across Every Vertical</h2>
            <p className="bc-section-sub">
              From regulated healthcare environments to global financial institutions,
              VMware by Broadcom delivers the security, compliance, and performance enterprises demand.
            </p>
          </div>
          <div className="bc-industries__grid">
            {industries.map((ind) => (
              <div key={ind.name} className="bc-industry-card">
                <span className="bc-industry-card__icon">{ind.icon}</span>
                <h3 className="bc-industry-card__name">{ind.name}</h3>
                <p className="bc-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Architecture ── */}
      <section className="bc-arch">
        <div className="bc-shell">
          <div className="bc-arch__inner">
            <div className="bc-arch__content">
              <span className="bc-section-badge"><span className="bc-dot" /> VCF Deep Dive</span>
              <h2 className="bc-arch__title">VMware Cloud Foundation — The All-in-One Private Cloud</h2>
              <p className="bc-arch__body">
                VMware Cloud Foundation (VCF) brings together vSphere, vSAN, NSX, and Aria in a single,
                integrated private cloud platform. Deploy and operate a consistent, governed private cloud
                from a single operational model — on-premises, at the edge, or in a hosted data center.
              </p>
              <p className="bc-arch__body">
                ALG Data Guard's VCAP/VCP-certified engineers handle VCF planning, Workload Domain
                configuration, SDDC Manager setup, and Day-2 operations so your private cloud runs
                at its full potential from day one.
              </p>
              <div className="bc-arch__capabilities">
                {["SDDC Manager", "Workload Domains", "vLCM Lifecycle", "Cloud Builder", "NSX Federation", "Multi-Site Stretch", "Principal Storage", "vSAN Witness"].map((c) => (
                  <span key={c} className="bc-arch__cap">{c}</span>
                ))}
              </div>
              <button className="bc-btn-primary" style={{ background: "#cc0000", color: "#fff" }} onClick={() => navigate("/contact")}>
                Plan VCF Deployment <ArrowIcon />
              </button>
            </div>
            <div className="bc-arch__visual">
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
                alt="VMware Cloud Foundation data center"
                className="bc-arch__img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Timeline ── */}
      <section className="bc-timeline">
        <div className="bc-shell">
          <div className="bc-timeline__head">
            <span className="bc-section-badge"><span className="bc-dot" /> Deployment Process</span>
            <h2 className="bc-section-title">Our Proven Delivery Methodology</h2>
            <p className="bc-section-sub">
              A structured, risk-managed approach to VMware infrastructure delivery —
              from initial discovery through to production-ready operations.
            </p>
          </div>
          <div className="bc-timeline__steps">
            {deploymentSteps.map((step) => (
              <div key={step.num} className="bc-timeline__step">
                <div className="bc-timeline__num">
                  <span className="bc-timeline__step-icon">{step.icon}</span>
                </div>
                <h3 className="bc-timeline__step-title">{step.title}</h3>
                <p className="bc-timeline__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Business Benefits ── */}
      <section className="bc-benefits">
        <div className="bc-shell">
          <div className="bc-benefits__inner">
            <div className="bc-benefits__img-wrap">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80"
                alt="Enterprise IT team"
                className="bc-benefits__img"
              />
              <div className="bc-benefits__img-overlay" />
            </div>
            <div className="bc-benefits__content">
              <span className="bc-section-badge" style={{ background: "rgba(204,0,0,0.2)", borderColor: "rgba(204,0,0,0.4)", color: "#ff6666" }}>
                <span className="bc-dot" /> Business Benefits
              </span>
              <h2 className="bc-section-title">Measurable ROI from Your VMware Investment</h2>
              <p className="bc-section-sub">
                VMware by Broadcom delivers quantifiable business outcomes across performance,
                cost efficiency, security, and operational agility.
              </p>
              <div className="bc-benefits__list">
                {benefits.map((b) => (
                  <div key={b.title} className="bc-benefit-item">
                    <div className="bc-benefit-item__icon">{b.icon}</div>
                    <div>
                      <div className="bc-benefit-item__title">{b.title}</div>
                      <p className="bc-benefit-item__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Success Metrics ── */}
      <section className="bc-metrics" ref={metricsRef}>
        <div className="bc-shell">
          <div className="bc-metrics__inner">
            <div className="bc-metric">
              <div className="bc-metric__value">{c1}+</div>
              <div className="bc-metric__label">VMware Projects Delivered</div>
            </div>
            <div className="bc-metric">
              <div className="bc-metric__value">{c2}%</div>
              <div className="bc-metric__label">Customer Satisfaction Score</div>
            </div>
            <div className="bc-metric">
              <div className="bc-metric__value">{c3}+</div>
              <div className="bc-metric__label">VMs Successfully Migrated</div>
            </div>
            <div className="bc-metric">
              <div className="bc-metric__value">{c4}/7</div>
              <div className="bc-metric__label">VMware Support Availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="bc-certs">
        <div className="bc-shell">
          <div className="bc-certs__head">
            <span className="bc-section-badge"><span className="bc-dot" /> Partner Certifications</span>
            <h2 className="bc-section-title">Our VMware Credentials</h2>
            <p className="bc-section-sub">
              ALG Data Guard maintains the highest level of VMware certifications,
              ensuring every engagement is delivered by qualified, practicing engineers.
            </p>
          </div>
          <div className="bc-certs__grid">
            {certifications.map((c) => (
              <div key={c.name} className="bc-cert-card">
                <div className="bc-cert-card__badge">{c.badge}</div>
                <div className="bc-cert-card__name">{c.name}</div>
                <div className="bc-cert-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="bc-usecases">
        <div className="bc-shell">
          <div className="bc-usecases__inner">
            <div>
              <span className="bc-section-badge"><span className="bc-dot" /> Real-World Use Cases</span>
              <h2 className="bc-section-title" style={{ marginBottom: "1rem" }}>VMware in Action Across Your Business</h2>
              <p className="bc-section-sub" style={{ marginBottom: "2rem" }}>
                Real-world implementation scenarios where VMware by Broadcom delivers transformative value
                for enterprise organizations of every size and industry.
              </p>
              <img
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=800&q=80"
                alt="Enterprise IT professional"
                className="bc-usecases__img"
              />
            </div>
            <div className="bc-usecases__cards">
              {useCases.map((uc) => (
                <div key={uc.title} className="bc-usecase-card">
                  <div className="bc-usecase-card__title">
                    <span className="bc-usecase-card__dot" />
                    {uc.title}
                  </div>
                  <p className="bc-usecase-card__desc">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bc-faq">
        <div className="bc-shell">
          <div className="bc-faq__head">
            <span className="bc-section-badge" style={{ background: "rgba(204,0,0,0.2)", borderColor: "rgba(204,0,0,0.4)", color: "#ff6666" }}>
              <span className="bc-dot" /> Frequently Asked Questions
            </span>
            <h2 className="bc-section-title">Common VMware Questions, Answered</h2>
            <p className="bc-section-sub">
              Everything you need to know about VMware by Broadcom and how ALG Data Guard delivers it.
            </p>
          </div>
          <div className="bc-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`bc-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="bc-faq-item__trigger" onClick={() => toggleFaq(i)} aria-expanded={openFaq === i}>
                  <span className="bc-faq-item__q">{faq.q}</span>
                  <span className="bc-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="bc-faq-item__answer">
                  <div className="bc-faq-item__answer-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bc-cta">
        <div className="bc-cta__inner">
          <span className="bc-section-badge" style={{ background: "rgba(204,0,0,0.25)", borderColor: "rgba(204,0,0,0.5)", color: "#ff8888", margin: "0 auto 1.2rem" }}>
            <span className="bc-dot" /> Get Started Today
          </span>
          <h2 className="bc-cta__title">
            Modernize Your Infrastructure with ALG Data Guard and Broadcom
          </h2>
          <p className="bc-cta__sub">
            Our VMware-certified engineers are ready to assess, design, and deploy the right
            virtualization and cloud solution for your organization — from Day 0 planning to
            Day 2 operations and beyond.
          </p>
          <div className="bc-cta__actions">
            <button className="bc-btn-primary" onClick={() => navigate("/contact")}>
              Get Free VMware Assessment <ArrowIcon />
            </button>
            <button className="bc-btn-outline" onClick={() => navigate("/contact")}>
              Download VCF Brief
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
